import { useState } from 'react';
import { useMachine } from '@xstate/react';
import { useParams } from 'react-router-dom';
import { ProductType } from '@tink/core/src/types';
import {
  createHandoffConsumerMachine,
  ERROR_INVALID_HANDOFF_PARAMETER,
} from '../machines/handoffConsumerMachine/handoffConsumerMachine';
import { useUrlQuery } from '../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { useAppSelector } from '../../stores/redux-store';
import { HandoffCredentialsAction } from '../../models/handoffSession';
import { applicationParameters } from '../../utilities/application-parameters';
import { useProduct } from '../../components/ApplicationProviders/ProductProvider/ProductProvider';
import { useSelectedAuthenticationOptions } from '../../payments/hooks/useSelectedAuthenticationOptions/useSelectedAuthenticationOptions';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';

export const useHandoffConsumerMachine = () => {
  const { credentialsId, credentialsIdFromTPP } = useUrlQuery();
  const { credentialsAction } = useParams<{
    credentialsAction: HandoffCredentialsAction;
  }>();
  const { productNames, productType } = useProduct();
  const handoffSession = useAppSelector((state) => state.handoffSession.data);

  if (!handoffSession || !('userAccessToken' in handoffSession)) {
    throw new ApplicationError(ERROR_INVALID_HANDOFF_PARAMETER);
  }
  const reportRefreshItems = useAppSelector(
    (state) => state.reports.reportDescription?.refreshItems
  );
  const { selectedAuthenticationOptions } = useSelectedAuthenticationOptions({
    name: handoffSession.providerName,
  });
  const [machine] = useState(
    createHandoffConsumerMachine({
      initialContext: {
        credentialsIdFromTPP,
        handoffSession,
        credentialsAction,
      },
      productType,
      productNames,
      credentialsId,
      reportRefreshItems,
      triggerRefresh: productType !== ProductType.PAY,
      scope: applicationParameters.get('scope') || '',
      refreshableItems: applicationParameters.get('refreshableItems'),
      authenticate: applicationParameters.get('authenticate'),
      selectedAuthenticationOptions,
    })
  );

  return useMachine(machine);
};
